import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Link } from "react-router-dom";

import { Typography } from "@mui/material";
import InfiniteScroll from "react-infinite-scroll-component";

// Actions
import { fetchNotificationsPage, resetAllNotifications } from "../../actions/notifications";

// Components
import NotificationManager from "./NotificationManager";
import Spinner from "../layout/Spinner";
import Button from "@mui/material/Button";

// Feed-standard Pull Down/Release to Refresh
import PullDownRefresh from "../feed/components/PullDownRefresh";
import ReleaseToRefresh from "../feed/components/ReleaseToRefresh";

// Icons
import { ReactComponent as SearchIcon } from "../../assets/icons/svg/search.svg";
import { ReactComponent as InviteIcon } from "../../assets/icons/svg/user-plus-solid.svg";
import { ReactComponent as NotificationDing } from "../../assets/icons/svg/notification-ding.svg";

const NotificationsPage = ({
  notifications: { unread, notifications, error, loading, hasMore },
  fetchNotificationsPage,
  resetAllNotifications,
}) => {
  // Fetch first page on mount
  useEffect(() => {
    fetchNotificationsPage(0, 20);
    return () => {
      resetAllNotifications();
    };
  }, [fetchNotificationsPage, resetAllNotifications]);

  // Fetch next page
  const fetchMoreData = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotificationsPage(notifications.length, 20);
    }
  }, [loading, hasMore, notifications.length, fetchNotificationsPage]);

  // Pull down to refresh
  const refreshNotifications = useCallback(() => {
    resetAllNotifications();
    fetchNotificationsPage(0, 20);
  }, [resetAllNotifications, fetchNotificationsPage]);

  return (
    <div className="w-full py-[4rem] md:w-[calc(100%-5rem)] md:ml-[5rem] md:pt-0 bg-neutral-50">
      <div className="m-auto md:max-w-[750px] md:px-8 min-h-screen md:pt-8">
        <div className="p-4 bg-background border border-separator md:rounded-md shadow-sm w-full flex flex-row items-start justify-between">
          <div className="flex flex-col items-start">
            <Typography variant="h6" className="font-normal">
              Notifications
            </Typography>
            {unread && unread > 0 ? (
              <div className="flex items-center flex-row">
                <NotificationDing className="inline-block w-4 h-4 fill-rose-600 mr-2" />
                <Typography variant="subtitle2" className="text-rose-600 leading-none font-normal text-xs">
                  {`${unread.toLocaleString()} unread`}
                </Typography>
              </div>
            ) : null}
          </div>
          <div className="flex flex-col items-end gap-1">
            <Link to="/search?type=profiles">
              <Button
                variant="outlined"
                disableRipple
                className="border border-secondaryText-200 hover:border-secondaryText"
                size="small"
              >
                <SearchIcon className="h-3 mr-2 fill-secondaryText" />
                <Typography variant="caption" component="p">
                  Find users to follow
                </Typography>
              </Button>
            </Link>
            {navigator.share && (
              <Button
                className="hover:bg-transparent"
                size="small"
                disableRipple
                onClick={() => {
                  navigator
                    .share({
                      title: "Join me on thisPantry",
                      text: "Bring all your recipes together with thisPantry. Generate new recipes, manage your pantry, and gain valuable insights on expenses and nutrition. Simplify your cooking and make every ingredient count with thisPantry.",
                      url: "https://www.thispantry.com",
                    })
                    .catch((error) => console.log("Error sharing", error));
                }}
              >
                <InviteIcon className="h-3 mr-2 fill-secondaryText" />
                <Typography variant="caption" component="p">
                  Invite&nbsp;Friends
                </Typography>
              </Button>
            )}
          </div>
        </div>
        <div className="mt-8">
          {error ? (
            <div className="h-full flex justify-center items-center text-center py-8 px-4">
              <Typography variant="subtitle1">An error has occurred. Please refresh the page and try again.</Typography>
            </div>
          ) : (
            <>
              {notifications.length > 0 ? (
                <InfiniteScroll
                  dataLength={notifications.length}
                  next={fetchMoreData}
                  hasMore={hasMore}
                  loader={<Spinner />}
                  pullDownToRefresh
                  refreshFunction={refreshNotifications}
                  pullDownToRefreshThreshold={75}
                  pullDownToRefreshContent={<PullDownRefresh />}
                  releaseToRefreshContent={<ReleaseToRefresh />}
                >
                  <div className="flex flex-col md:gap-2">
                    {notifications.map((notification, i) => (
                      <NotificationManager
                        key={notification._id}
                        notification={notification}
                        index={i}
                        isLast={i === notifications.length - 1}
                      />
                    ))}
                  </div>
                </InfiniteScroll>
              ) : loading ? (
                <Spinner />
              ) : (
                <div className="text-center py-8 px-4">
                  <Typography variant="subtitle1">No notifications yet!</Typography>
                  <div className="flex flex-col sm:flex-row gap-2 mt-4 justify-center">
                    <Link to="/search?type=profiles">
                      <Button
                        variant="outlined"
                        disableRipple
                        className="border border-secondaryText-200 hover:border-secondaryText"
                        size="small"
                      >
                        <SearchIcon className="h-3 mr-2 fill-secondaryText" />
                        <Typography variant="caption" component="p">
                          Find users to follow
                        </Typography>
                      </Button>
                    </Link>
                    {navigator.share && (
                      <Button
                        className="hover:bg-transparent"
                        size="small"
                        disableRipple
                        onClick={() => {
                          navigator
                            .share({
                              title: "Join me on thisPantry",
                              text: "Bring all your recipes together with thisPantry. Generate new recipes, manage your pantry, and gain valuable insights on expenses and nutrition. Simplify your cooking and make every ingredient count with thisPantry.",
                              url: "https://www.thispantry.com",
                            })
                            .catch((error) => console.log("Error sharing", error));
                        }}
                      >
                        <InviteIcon className="h-3 mr-2 fill-secondaryText" />
                        <Typography variant="caption" component="p">
                          Invite&nbsp;Friends
                        </Typography>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

NotificationsPage.propTypes = {
  notifications: PropTypes.object.isRequired,
  fetchNotificationsPage: PropTypes.func.isRequired,
  resetAllNotifications: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  notifications: state.notifications,
});

export default connect(mapStateToProps, { fetchNotificationsPage, resetAllNotifications })(NotificationsPage);
